import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { darken, lighten, clsx, safeRest } from '@wds/utils';
import { createUseStyles, useTheme } from 'react-jss';

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

var SEMANTIC_COLORS = Object.freeze(['primary', 'success', 'info', 'warning', 'upgrade', 'secondary', 'muted']);
Object.freeze(['dark', 'light', 'darkMuted', 'lightMuted', 'link']);
var SIZES = Object.freeze(['lg', 'md', 'sm']);
Object.freeze(['primary', 'upgrade', 'info', 'success', 'warning', 'secondary']);
Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));
Object.freeze(['md', 'sm']);
Object.freeze(['success', 'warning']);
Object.freeze(['success', 'warning']);
Object.freeze(['data-one', 'data-two', 'data-three', 'data-four', 'data-five', 'data-six', 'data-seven', 'data-eight', 'data-nine', 'data-ten']);
Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

Object.freeze({
  DOWN: 40,
  END: 35,
  ESC: 27,
  HOME: 36,
  LEFT: 37,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  RIGHT: 39,
  UP: 38,
  ENTER: 13,
  SPACE: 32,
  BACKSPACE: 8,
  TAB: 9
});
Object.freeze({
  37: -1,
  38: -1,
  39: 1,
  40: 1
});
Object.freeze(['top', 'bottom', 'right', 'left']);
Object.freeze(['primary', 'secondary', 'muted', 'success', 'warning', 'upgrade', 'info']);
Object.freeze(['light', 'dark']);
Object.freeze(['primary', 'light', 'dark', 'transparent']);
Object.freeze(['left', 'right', 'even', 'center']);
Object.freeze(['normal', 'near', 'far']);
Object.freeze(['up', 'right', 'down', 'left']);
Object.freeze(['slow', 'normal', 'fast']);
Object.freeze({
  speed: {
    slow: 300,
    normal: 200,
    fast: 100
  }
});
Object.freeze({
  speed: {
    slow: 450,
    normal: 350,
    fast: 250
  },
  distance: {
    normal: 25,
    near: 5,
    far: 75
  }
});

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var useStyles = createUseStyles(function (theme) {
  var type = theme.type,
      radius = theme.radius,
      shadow = theme.shadow,
      palette = theme.palette,
      spacing = theme.spacing;
  var button = {
    height: {
      sm: 30,
      md: 40,
      lg: 50
    },
    fontSize: {
      sm: type.fontSize.bodySm,
      md: type.fontSize.body,
      lg: type.fontSize.body
    },
    padding: {
      sm: spacing[3],
      md: spacing[5],
      lg: spacing[6] + spacing[1] // 36

    },
    borderRadius: radius.small,
    boxShadow: shadow.none
  };

  var variantSolid = function variantSolid(textColor, color, isPressed) {
    return {
      color: textColor,
      borderColor: isPressed,
      backgroundColor: isPressed,
      '&:hover, &:focus': {
        borderColor: lighten(0.03, color),
        backgroundColor: lighten(0.03, color)
      },
      '&:active': {
        borderColor: color,
        backgroundColor: color
      }
    };
  };

  var variantGhost = function variantGhost(backgroundColor, isPressed) {
    return {
      color: isPressed,
      borderColor: isPressed,
      backgroundColor: 'transparent',
      '&:hover, &:focus': {
        backgroundColor: backgroundColor
      },
      '&:active': {
        borderColor: isPressed
      }
    };
  };

  var variantText = function variantText(backgroundColor, isPressed) {
    return {
      color: isPressed,
      borderColor: 'transparent',
      backgroundColor: 'transparent',
      '&:hover, &:focus': {
        borderColor: backgroundColor,
        backgroundColor: backgroundColor
      }
    };
  };

  var colorAlt = function colorAlt() {
    return {
      color: palette.text.dark,
      borderColor: palette.text.dark,
      backgroundColor: palette.background.light,
      '&:hover, &:focus': {
        borderColor: palette.text.dark,
        backgroundColor: palette.background.main
      }
    };
  };

  var svgMargin = function svgMargin(leftIcon, rightIcon, size) {
    var iconMargin = function iconMargin(s) {
      return {
        sm: '0.5em',
        md: '0.8em',
        lg: '1em'
      }[s];
    };

    return {
      '& > svg': _objectSpread(_objectSpread({}, leftIcon && {
        marginRight: "".concat(iconMargin(size))
      }), rightIcon && {
        marginLeft: "".concat(iconMargin(size))
      })
    };
  };

  var iconPadding = function iconPadding(size) {
    return {
      sm: spacing[2],
      md: spacing[4],
      lg: spacing[5]
    }[size];
  };

  return {
    wrenchButton: function wrenchButton(_ref) {
      var color = _ref.color,
          variant = _ref.variant,
          size = _ref.size,
          pressed = _ref.pressed,
          stretched = _ref.stretched,
          iconOnly = _ref.iconOnly,
          leftIcon = _ref.leftIcon,
          rightIcon = _ref.rightIcon;
      var buttonColor = palette[color] && palette[color].main || palette.text[color] || palette.text.dark; // custom 'alt' color only used for button

      var isPressed = pressed ? darken(0.03, buttonColor) : buttonColor;
      var isStretched = stretched ? {
        display: 'flex',
        width: '100%'
      } : {
        display: 'inline-flex'
      };
      return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, isStretched), {}, {
        fontWeight: type.fontWeight.medium,
        fontFamily: type.fontFamily.base,
        '-webkit-font-smoothing': 'antialiased',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        position: 'relative',
        borderRadius: button.borderRadius,
        textDecoration: 'none',
        border: '1px solid transparent',
        boxShadow: button.boxShadow,
        transition: 'color 0.4s, border-color 0.4s, background-color 0.4s',
        padding: [0, iconOnly ? iconPadding(size) : button.padding[size]],
        fontSize: button.fontSize[size],
        height: button.height[size],
        '&:hover, &:focus': {
          transition: 'none',
          textDecoration: 'none'
        },
        '&:active': {
          transition: 'none',
          outline: 'none'
        },
        '&[disabled]': {
          pointerEvents: 'none',
          opacity: '0.4'
        }
      }, variant === 'solid' && variantSolid(palette[color] && palette[color].contrast, buttonColor, isPressed)), variant === 'ghost' && variantGhost(palette.background.accent, isPressed)), variant === 'text' && variantText(palette.background.accent, isPressed)), (leftIcon || rightIcon) && svgMargin(leftIcon, rightIcon, size)), color === 'alt' && colorAlt());
    }
  };
});
var _excluded = ["ariaPressed", "buttonType", "children", "color", "disabled", "href", "label", "size", "stretched", "variant"];
var BUTTON_TYPES = Object.freeze(['button', 'reset', 'submit']);
var VARIANTS = Object.freeze(['solid', 'ghost', 'text']);
var BUTTON_COLORS = Object.freeze(['alt', 'link'].concat(_toConsumableArray(SEMANTIC_COLORS)));

var Button = function Button(_ref) {
  var ariaPressed = _ref.ariaPressed,
      buttonType = _ref.buttonType,
      children = _ref.children,
      color = _ref.color,
      disabled = _ref.disabled,
      href = _ref.href,
      label = _ref.label,
      size = _ref.size,
      stretched = _ref.stretched,
      variant = _ref.variant,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(ariaPressed),
      _useState2 = _slicedToArray(_useState, 2),
      pressed = _useState2[0],
      setPressed = _useState2[1];

  useEffect(function () {
    if (ariaPressed !== pressed) setPressed(!pressed);
  }, [ariaPressed, pressed]);
  var numberOfChildren = React.Children.count(children);
  var firstChildType = React.Children.toArray(children)[0].type;
  var lastChildType = React.Children.toArray(children)[numberOfChildren - 1].type;
  var iconOnly = numberOfChildren === 1 && firstChildType && firstChildType.displayName && firstChildType.displayName.includes('WithIcon');
  var leftIcon = numberOfChildren > 1 && firstChildType && firstChildType.displayName && firstChildType.displayName.includes('WithIcon');
  var rightIcon = numberOfChildren > 1 && lastChildType && lastChildType.displayName && lastChildType.displayName.includes('WithIcon'); // https://mathiasbynens.github.io/rel-noopener/

  var autoAddRel = rest.target && rest.target === '_blank' && !rest.rel;
  var rel = autoAddRel && 'noopener noreferrer';
  var theme = useTheme();

  var _useStyles = useStyles({
    color: color,
    variant: variant,
    size: size,
    pressed: pressed,
    stretched: stretched,
    theme: theme,
    iconOnly: iconOnly,
    leftIcon: leftIcon,
    rightIcon: rightIcon
  }),
      wrenchButton = _useStyles.wrenchButton;

  var classNames = clsx(wrenchButton, // Static class used for internal reference
  // Ex. ButtonGroup conditional styles
  "wrenchButton--".concat(variant));
  /* eslint-disable react/button-has-type */

  return /*#__PURE__*/React.createElement(React.Fragment, null, href ? /*#__PURE__*/React.createElement("a", Object.assign({}, safeRest(rest), {
    className: classNames,
    href: href,
    title: label,
    rel: rel
  }), children) : /*#__PURE__*/React.createElement("button", Object.assign({}, safeRest(rest), {
    className: classNames,
    type: buttonType,
    disabled: disabled,
    "aria-pressed": ariaPressed,
    "aria-label": label
  }), children));
};

Button.defaultProps = {
  ariaPressed: false,
  buttonType: 'button',
  color: 'primary',
  disabled: false,
  href: undefined,
  label: undefined,
  size: 'md',
  stretched: false,
  variant: 'solid'
};
Button.propTypes = {
  /**
   * Aria pressed state of Button
   */
  ariaPressed: PropTypes.bool,

  /**
   * Type attribute of button element
   */
  buttonType: PropTypes.oneOf(BUTTON_TYPES),

  /**
   * Text, icons for Button
   */
  children: PropTypes.node.isRequired,

  /**
   * Set background color of the button
   */
  color: PropTypes.oneOf(BUTTON_COLORS),

  /**
   * If true button will be disabled
   */
  disabled: PropTypes.bool,

  /**
   * Use if the button is a link to a url.
   */
  href: PropTypes.string,

  /**
   * Label for title or aria-label depending on the type of button (<a> or <button> element).
   */
  // eslint-disable-next-line consistent-return
  label: function label(props, propName) {
    // we only need to see if it's an Icon since other cases could be valid
    var searchTerm = 'WithIcon';
    var typeCheck = false; // props.children could be an array or not

    if (Array.isArray(props.children)) {
      typeCheck = props.children.some(function (child) {
        return child.type && child.type.displayName && child.type.displayName.indexOf(searchTerm) > -1;
      });
    } else {
      typeCheck = props.children.type && props.children.type.displayName && props.children.type.displayName.indexOf(searchTerm) > -1;
    }

    if (props[propName] === undefined && typeCheck) {
      return new Error('A label is required when the child of a Button is a React component');
    }
  },

  /**
   * Size variants for Button
   */
  size: PropTypes.oneOf(SIZES),

  /**
   * Stretch the width of the button to match its container
   */
  stretched: PropTypes.bool,

  /**
   * Choose what button style variant to display.
   */
  variant: PropTypes.oneOf(VARIANTS)
};
Button.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Button",
  "props": {
    "ariaPressed": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Aria pressed state of Button"
    },
    "buttonType": {
      "defaultValue": {
        "value": "'button'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "BUTTON_TYPES"
      },
      "required": false,
      "description": "Type attribute of button element"
    },
    "color": {
      "defaultValue": {
        "value": "'primary'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "BUTTON_COLORS"
      },
      "required": false,
      "description": "Set background color of the button"
    },
    "disabled": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "If true button will be disabled"
    },
    "href": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Use if the button is a link to a url."
    },
    "label": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "custom",
        "raw": "(props, propName) => {\n  // we only need to see if it's an Icon since other cases could be valid\n  const searchTerm = 'WithIcon';\n  let typeCheck = false;\n  // props.children could be an array or not\n  if (Array.isArray(props.children)) {\n    typeCheck = props.children.some(\n      (child) =>\n        child.type &&\n        child.type.displayName &&\n        child.type.displayName.indexOf(searchTerm) > -1\n    );\n  } else {\n    typeCheck =\n      props.children.type &&\n      props.children.type.displayName &&\n      props.children.type.displayName.indexOf(searchTerm) > -1;\n  }\n\n  if (props[propName] === undefined && typeCheck) {\n    return new Error(\n      'A label is required when the child of a Button is a React component'\n    );\n  }\n}"
      },
      "required": false,
      "description": "Label for title or aria-label depending on the type of button (<a> or <button> element)."
    },
    "size": {
      "defaultValue": {
        "value": "'md'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "SIZES"
      },
      "required": false,
      "description": "Size variants for Button"
    },
    "stretched": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Stretch the width of the button to match its container"
    },
    "variant": {
      "defaultValue": {
        "value": "'solid'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "VARIANTS"
      },
      "required": false,
      "description": "Choose what button style variant to display."
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "Text, icons for Button"
    }
  }
};
export { Button };